/**
 * Header Class
 */
export class Header {

	/**
	 * Header element.
	 * @type {HTMLElement|null}
	 */
	#header;

	/**
	 * Navigation element.
	 * @type {HTMLElement|null}
	 */
	#navigation;

	/**
	 * Progressbar element.
	 * @type {HTMLElement|null}
	 */
	#progressbar;

	/**
	 * Navigation menu <ul> element.
	 * @type {HTMLElement|null}
	 */
	#navMenu;

	/**
	 * Hamburger menu button.
	 * @type {HTMLElement|null}
	 */
	#hamburgerMenu;

	/**
	 * NavExtras searchbar element.
	 * @type {HTMLElement|null}
	 */
	#navExtras;

	/**
	 * Checks if device is in mobile mode.
	 * @type {boolean}
	 */
	#isMobile = false;

	/**
	 * Stores a reference to the root HTML element.
	 * @type {HTMLElement|null}
	 */
	#root;

	/**
	 * Stores a reference to the body HTML element.
	 * @type {HTMLElement|null}
	 */
	#body;

	/**
	 * Represents the vertical scroll position.
	 * @type {number}
	 */
	#scrollY = 0;

	/**
	 * Creates an instance of a class.
	 *
	 * @return {void} Returns nothing.
	 */
	constructor() {

		const mobileBreakpoint = getComputedStyle( document.documentElement )
			.getPropertyValue( '--mobile-breakpoint' )
			.trim();
		this.#isMobile = window.matchMedia(
			`(max-width: ${mobileBreakpoint})`
		).matches;
		this.#header = document.querySelector( '.b-header' );
		this.#hamburgerMenu = this.#header?.querySelector( '.c-hamburger' );

		this.#navExtras = this.#header.querySelector( '.b-nav-extras' );

		this.#navigation = this.#header?.querySelector( '.b-nav' );
		this.#progressbar = this.#header?.querySelector(
			'.b-header__progressbar-container'
		);
		this.#navMenu = this.#navigation?.querySelector( '.b-nav__menu' );
		this.#root = document.querySelector( ':root' );
		this.#body = document.querySelector( 'body' );

		if ( document.readyState === 'loading' ) {

			window.addEventListener( 'load', () => this.init() );

		} else {

			this.init();

		}

	}

	/**
	 * Initializes header functionality.
	 *
	 * @return {void} Returns nothing.
	 */
	init = () => {

		requestAnimationFrame( () => {

			this.setMainStyles();
			this.handleScrollClass();

		} );
		this.toggleMobileMenu();
		this.toggleDropdownMenu();
		this.toggleMobileSubmenu();
		document.addEventListener( 'click', this.handleClickOutside );
		window.addEventListener( 'scroll', this.handleScrollClass );
		if ( this.#progressbar ) {

			window.addEventListener( 'scroll', this.updateProgress );

		}

	};

	/**
	 * Update the progress bar based on the scroll position of the document.
	 * Calculates the percentage of page scrolled and adjusts the width of the progress bar accordingly.
	 */
	updateProgress = () => {

		const { scrollTop, scrollHeight, clientHeight } =
			document.documentElement;
		const scrolled = ( scrollTop / ( scrollHeight - clientHeight ) ) * 100;
		const progressbarLine =
			this.#progressbar.querySelector( '.js-progressbar' );
		progressbarLine.style.width = `${scrolled}%`;

	};

	/**
	 * Handles click events outside of a specific navigation element.
	 * @param {Event} event - The click event that triggered the handleClickOutside function.
	 */
	handleClickOutside = ( event ) => {

		if ( this.#navigation && this.#navigation.contains( event.target ) ) {

			return;

		}

		if ( this.#navigation ) {

			this.closeAllDropdowns();

		}

	};

	/**
	 * Function to add or remove a CSS class based on the scroll position of the window.
	 * If the scroll position is greater than or equal to 150, it adds the CSS class 'scrolled' to the body element.
	 * If the scroll position is less than 200, it removes the CSS class 'scrolled' from the body element.
	 */
	handleScrollClass = () => {

		if ( window.scrollY >= 30 ) {

			this.#body.classList.add( 'scrolled' );

		} else {

			this.#body.classList.remove( 'scrolled' );

		}

	};

	/**
	 * Sets header styles.
	 *
	 * @return {void} Returns nothing.
	 */
	setMainStyles = () => {

		const headerHeight = this.#header?.offsetHeight;

		if ( !headerHeight || !this.#root ) {

			return;

		}

		if ( this.#navigation ) {

			const openedDropdown = this.#navigation.querySelector(
				'.b-nav-dropdown--open'
			);
			if ( openedDropdown ) {

				requestAnimationFrame( () => {

					openedDropdown.style.setProperty(
						'--dropdown-height',
						`${openedDropdown.scrollHeight}px`
					);

				} );

			}

			const dropdownColumns = this.#navigation.querySelectorAll(
				'.b-nav-dropdown-column__inner'
			);

			dropdownColumns.forEach( ( column ) => {

				requestAnimationFrame( () => {

					column.style.setProperty(
						'--dropdown-content-height',
						`${column.scrollHeight}px`
					);

				} );

			} );

		}

	};

	/**
	 * Toggle mobile menu on click.
	 *
	 * @return {void} Returns nothing.
	 */
	toggleMobileMenu = () => {

		if ( !this.#hamburgerMenu ) {

			return;

		}

		this.#hamburgerMenu.addEventListener( 'click', () => {

			this.updateMobileMenuState();

		} );

	};

	/**
	 * Handles toggling the mobile menu and resets the top bar menu state.
	 * @return {void}
	 */
	updateMobileMenuState = () => {

		this.#navigation.classList.toggle( 'b-nav--open' );
		this.#hamburgerMenu.classList.toggle( 'c-hamburger--open' );
		this.#body.classList.toggle( 'no-scroll' );
		this.#body.classList.toggle( 'menu-mobile-is-open' );

		const isMenuOpen = this.#body.classList.contains( 'menu-mobile-is-open' );

		if ( isMenuOpen ) {

			this.injectMobileHeader();

			this.#scrollY = window.scrollY;
			this.#body.style.position = 'fixed';
			this.#body.style.top = `-${this.#scrollY}px`;
			this.#body.style.width = '100%';
			this.#body.style.maxHeight = '100vh';
			this.#body.style.overflowY = 'hidden';
			document.documentElement.classList.add( 'no-scroll' );
			this.updateMobilePadding();

		} else {

			this.#body.style.position = '';
			this.#body.style.top = '';
			this.#body.style.width = '';
			this.#body.style.maxHeight = '';
			this.#body.style.overflowY = '';
			window.scrollTo( 0, this.#scrollY );
			document.documentElement.classList.remove( 'no-scroll' );
			this.#navigation.style.removeProperty( '--mobile-extra-padding' );

		}

	};

	/**
	 *
	 */
	toggleDropdownMenu = () => {

		if ( !this.#navigation ) {

			return;

		}
		this.#navigation.addEventListener( 'click', ( e ) => {

			const menuItemLink = e.target.closest(
				'.b-nav-item__link.has-dropdown'
			);
			if ( menuItemLink ) {

				const isOpen = menuItemLink.classList.contains(
					'b-nav-item__link--open'
				);
				this.closeAllDropdowns();
				if ( !isOpen ) {

					this.openDropdown( menuItemLink );

				}

			}

		} );

	};

	/**
	 * Closes all dropdown menus in the navigation menu.
	 * Removes 'menu-is-open' class from body element and closes all dropdowns in the navigation menu.
	 * Also resets the classes and attributes of dropdown links and items.
	 *
	 * @return {void}
	 */
	closeAllDropdowns() {

		this.#body.classList.remove( 'menu-is-open' );
		const allLinks = this.#navigation.querySelectorAll(
			'.b-nav-item__link.has-dropdown'
		);
		const allItems = this.#navigation.querySelectorAll( '.b-nav-item' );
		const allDropdowns =
			this.#navigation.querySelectorAll( '.b-nav-dropdown' );
		const backButtons = this.#navigation.querySelectorAll(
			'.b-nav-dropdown__back-button'
		);

		allLinks.forEach( ( link ) => {

			link.classList.remove( 'b-nav-item__link--open' );
			link.setAttribute( 'aria-expanded', 'false' );

		} );
		allItems.forEach( ( item ) => {

			item.classList.remove( 'b-nav-item--open' );
			item.classList.remove( 'b-nav-item--simple' );

		} );
		allDropdowns.forEach( ( dropdown ) => {

			dropdown.classList.remove( 'b-nav-dropdown--open' );
			dropdown.style.removeProperty( '--dropdown-height' );
			dropdown.setAttribute( 'inert', '' );

		} );

		setTimeout( () => {

			backButtons.forEach( ( btn ) => btn.remove() );

		}, 300 );

	}

	/**
	 * Opens the dropdown menu related to the specified menu item link element.
	 *
	 * @param {HTMLElement} menuItemLink - The menu item link element that triggers the dropdown opening.
	 *
	 * @return {void}
	 */
	openDropdown( menuItemLink ) {

		this.#body.classList.add( 'menu-is-open' );
		menuItemLink.classList.add( 'b-nav-item__link--open' );
		menuItemLink.setAttribute( 'aria-expanded', 'true' );

		const menuItem = menuItemLink.closest( '.b-nav-item' );
		menuItem.classList.add( 'b-nav-item--open' );

		const dropdown = menuItem.querySelector( '.b-nav-dropdown' );
		// ADD SIMPLE CLASS
		if ( dropdown && dropdown.classList.contains( 'is-layout-simple' ) ) {

			menuItem.classList.add( 'b-nav-item--simple' );

		}

		if ( dropdown ) {

			dropdown.removeAttribute( 'inert' );
			dropdown.classList.add( 'b-nav-dropdown--open' );
			requestAnimationFrame( () => {

				dropdown.style.setProperty(
					'--dropdown-height',
					`${dropdown.scrollHeight}px`
				);

			} );

		}

	}


	/**
	 * Toggles the visibility of the mobile submenu when a menu item is clicked.
	 *
	 * @return {void} Returns nothing.
	 */
	toggleMobileSubmenu = () => {

		if ( !this.#navigation ) {

			return;

		}

		this.#navigation.addEventListener( 'click', ( e ) => {

			if ( !this.#isMobile ) {

				return;

			}

			const openedDropdown = this.#navigation.querySelector(
				'.b-nav-dropdown--open'
			);

			if ( !openedDropdown ) {

				return;

			}
			const dropdownButton = e.target.closest(
				'.b-nav-dropdown-column__title-wrapper'
			);

			if ( dropdownButton ) {

				dropdownButton.classList.toggle(
					'b-nav-dropdown-column__title-wrapper--open'
				);
				if (
					dropdownButton.classList.contains(
						'b-nav-dropdown-column__title-wrapper--open'
					)
				) {

					dropdownButton.setAttribute( 'aria-expanded', 'true' );

				} else {

					dropdownButton.setAttribute( 'aria-expanded', 'false' );

				}

				const dropdownContent = dropdownButton
					.closest( '.b-nav-dropdown-column' )
					.querySelector( '.b-nav-dropdown-column__inner' );

				if ( dropdownContent ) {

					dropdownContent.classList.toggle(
						'b-nav-dropdown-column__inner--open'
					);

					if (
						dropdownContent.classList.contains(
							'b-nav-dropdown-column__inner--open'
						)
					) {

						requestAnimationFrame( () => {

							dropdownContent.style.setProperty(
								'--dropdown-content-height',
								`${dropdownContent.scrollHeight}px`
							);

						} );

					} else {

						dropdownContent.style.setProperty(
							'--dropdown-content-height',
							`0px`
						);

					}

					const dropdown = dropdownButton.closest( '.b-nav-dropdown' );
					this.updateDropdownHeight( dropdown );

				}

			}

		} );

	};

	/**
	 * Recalculates and updates the height of the open dropdown
	 * @param {HTMLElement} dropdown
	 */
	updateDropdownHeight( dropdown ) {

		if ( !dropdown ) {

			return;

		}

		requestAnimationFrame( () => {

			let total = 0;

			// Add height of all title wrappers
			dropdown.querySelectorAll( '.b-nav-dropdown-column__title-wrapper' )
				.forEach( el => total += el.offsetHeight );

			// Add height of opened inner contents
			dropdown.querySelectorAll( '.b-nav-dropdown-column__inner' )
				.forEach( inner => {

					if ( inner.classList.contains( 'b-nav-dropdown-column__inner--open' ) ) {

						total += inner.scrollHeight; // visible part only

					}

				} );

			dropdown.style.setProperty( '--dropdown-height', `${total}px` );

		} );

	}

	/**
	 * Injects mobile header into navigation if missing.
	 */
	injectMobileHeader() {

		if ( !this.#navigation ) {

			return;

		}

		// Check if header already exists
		const exists = this.#navigation.querySelector( '.b-nav__mob-header' );
		if ( exists ) {

			return;

		}

		// Find logo in main header
		const headerLogo = this.#header?.querySelector( '.b-nav-logo' );
		const clonedLogo = headerLogo ? headerLogo.cloneNode( true ) : null;

		// Create wrapper element
		const mobHeader = document.createElement( 'div' );
		mobHeader.classList.add( 'b-nav__mob-header' );

		// Append cloned logo
		if ( clonedLogo ) {

			mobHeader.appendChild( clonedLogo );

		}

		// Create close button
		const closeBtn = document.createElement( 'button' );
		closeBtn.classList.add( 'b-nav__close' );
		closeBtn.setAttribute( 'type', 'button' );
		closeBtn.setAttribute( 'aria-label', 'Close menu' );
		closeBtn.innerHTML = '';

		// Attach click handler to close menu
		closeBtn.addEventListener( 'click', () => {

			this.updateMobileMenuState();

		} );

		mobHeader.appendChild( closeBtn );

		// Insert at the beginning of .b-nav
		this.#navigation.prepend( mobHeader );

	}

	/**
	 * Resets navigation state on breakpoint changes.
	 */
	resetOnResize() {

		// Always close dropdowns
		this.closeAllDropdowns();

		// If leaving mobile mode → close mobile menu
		if ( !this.#isMobile && this.#navigation.classList.contains( 'b-nav--open' ) ) {

			this.updateMobileMenuState();

		}

		// Remove mobile header if not in mobile
		if ( !this.#isMobile ) {

			const mobHeader = this.#navigation.querySelector( '.b-nav__mob-header' );
			if ( mobHeader ) {

				mobHeader.remove();

			}

		}

	}

	/**
	 * Updates mobile padding variable based on .b-nav-extras height.
	 */
	updateMobilePadding() {

		if ( !this.#navExtras || !this.#navigation || !this.#isMobile ) {

			return;

		}

		requestAnimationFrame( () => {

			const extrasHeight = this.#navExtras.offsetHeight || 0;

			// Assign CSS variable on the navigation container
			this.#navigation.style.setProperty(
				'--mobile-extra-padding',
				`${extrasHeight}px`
			);

		} );

	}

}
